import { AnimatePresence, motion } from "framer-motion";
import { type ReactNode, createContext, useCallback, useContext, useRef, useState } from "react";

type ToastType = "success" | "error" | "warning" | "info";

type ToastItem = {
	id: string;
	type: ToastType;
	title: string;
	message?: string;
};

type AddToastInput = {
	type: ToastType;
	title: string;
	message?: string;
};

type ToastContextValue = {
	addToast: (input: AddToastInput) => void;
};

const TOAST_AUTO_DISMISS_MS = 4000;

const fallbackToast: ToastContextValue = {
	addToast: () => {
		// eslint-disable-next-line no-console -- Dev-time warning for missing ToastProvider
		console.error("useToast: addToast called outside <ToastProvider>. Toast will not render.");
	},
};

const ToastContext = createContext<ToastContextValue>(fallbackToast);

const typeStyles: Record<ToastType, string> = {
	success: "border-l-4 border-l-pastel-vibrant-green bg-green-50",
	error: "border-l-4 border-l-red-400 bg-red-50",
	warning: "border-l-4 border-l-pastel-vibrant-orange bg-yellow-50",
	info: "border-l-4 border-l-pastel-vibrant-blue bg-blue-50",
};

export function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<Array<ToastItem>>([]);
	const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
	const counterRef = useRef(0);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
		const timer = timersRef.current.get(id);
		if (timer) {
			clearTimeout(timer);
			timersRef.current.delete(id);
		}
	}, []);

	const addToast = useCallback(
		(input: AddToastInput) => {
			counterRef.current += 1;
			const id = `toast-${String(counterRef.current)}`;
			const newToast: ToastItem =
				input.message !== undefined
					? { id, type: input.type, title: input.title, message: input.message }
					: { id, type: input.type, title: input.title };

			setToasts((prev) => [...prev, newToast]);

			const timer = setTimeout(() => {
				removeToast(id);
			}, TOAST_AUTO_DISMISS_MS);
			timersRef.current.set(id, timer);
		},
		[removeToast]
	);

	return (
		<ToastContext.Provider value={{ addToast }}>
			{children}
			<div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80">
				<AnimatePresence>
					{toasts.map((toast) => (
						<motion.div
							key={toast.id}
							initial={{ opacity: 0, x: 100 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: 100 }}
							className={`rounded-lg p-4 shadow-pastel-md ${typeStyles[toast.type]}`}
						>
							<div className="flex items-start justify-between">
								<div>
									<p className="text-sm font-semibold text-pastel-black">{toast.title}</p>
									{toast.message && (
										<p className="text-xs text-pastel-gray mt-1">{toast.message}</p>
									)}
								</div>
								<button
									type="button"
									onClick={() => removeToast(toast.id)}
									className="ml-2 text-pastel-gray hover:text-pastel-black"
								>
									&times;
								</button>
							</div>
						</motion.div>
					))}
				</AnimatePresence>
			</div>
		</ToastContext.Provider>
	);
}

export function useToast(): ToastContextValue {
	return useContext(ToastContext);
}
